import { useEffect, useState } from 'react';
import { Table, Typography, Button, Modal, Upload, message, UploadProps, Tag } from 'antd';
import PageWrapper from '../../../ui/PageWrapper';
import { UploadOutlined } from '@ant-design/icons';
import useProfilePage from '../ProfilePage/useProfilePage';
import { SummerRetakeApplicationService } from './SummerRetakeApplicationService';

interface RetakeDisciplinesItem {
  disciplineId: number;
  discipline: string;
  fullname: string;
  studentId: number;
  totalGrade: number;
  volumeCredits: number;
}

interface RetakeStatusItem {
  id: number;
  fileName: string;
  createdDate: Date;
  approved: number;
  approvedDate: string | null;
  studentId: number | null;
  fileId: number;
  studentName: string;
  disciplineName: string;
  studentSurname: string;
  studentPatronymic: string | null;
  volumeCredits: number;
  comment: string;
  iin: string;
  disciplineId: number;
  approvedOfficeRegistrar: number;
}

interface Props {
  userId?: number;
}

const SummerRetakeDisciplines = ({ userId }: Props) => {
  const [retakeDisciplinesData, setRetakeDisciplinesData] = useState<RetakeDisciplinesItem[]>([]);
  const { profileInfo } = useProfilePage(userId);
  const [fileId, setFileId] = useState<number>();
  const [retakeStatusData, setRetakeStatusData] = useState<RetakeStatusItem[]>([]);
  const [showRetakeStatus, setShowRetakeStatus] = useState(false);
  const [showRetakeDisciplines, setShowRetakeDisciplines] = useState(true);
  const [totalSum, setTotalSum] = useState<number>(0);

  const fetchRetakeStatusData = () => {
    const studentId = profileInfo?.student.id;
    if (studentId) {
      SummerRetakeApplicationService.getRetakeStatusForStudent(studentId)
        .then((response) => {
          setRetakeStatusData(response.data);
        })
        .catch((error) => {
          console.error('Ошибка при получении статуса заявки:', error);
        });
    }
  };

  const fetchTotalCredits = () => {
    const studentId = profileInfo?.student.id;
    if (studentId) {
      SummerRetakeApplicationService.getTotalCreditsForCurrentYear(studentId)
        .then((response) => {
          setTotalSum(response.data ?? 0);
        })
        .catch((error) => {
          console.error('Ошибка при получении суммы кредитов:', error);
          setTotalSum(0);
        });
    }
  };

  const toggleRetakeDisciplines = () => {
    setShowRetakeDisciplines(true);
    setShowRetakeStatus(false);
    fetchRetakeDisciplinesData();
    fetchRetakeStatusData();
    fetchTotalCredits();
  };

  const toggleRetakeStatus = () => {
    setShowRetakeDisciplines(false);
    setShowRetakeStatus(true);
    fetchRetakeDisciplinesData();
    fetchRetakeStatusData();
    fetchTotalCredits();
  };

  useEffect(() => {
    fetchRetakeDisciplinesData();
  }, [profileInfo]);

  useEffect(() => {
    fetchTotalCredits();
  }, [profileInfo]);

  useEffect(() => {
    fetchRetakeStatusData();
  }, [profileInfo]);

  const fetchRetakeDisciplinesData = () => {
    const studentId = profileInfo?.student.id;
    if (studentId) {
      //SummerRetakeApplicationService.getRetakeDisciplinesForStudent(9273)
      SummerRetakeApplicationService.getRetakeDisciplinesForStudent(studentId)
        .then((response) => {
          setRetakeDisciplinesData(response.data);
        })
        .catch((error) => {
          console.error('Ошибка при получении данных:', error);
        });
    }
  };

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedDiscipline, setSelectedDiscipline] = useState<RetakeDisciplinesItem | null>(null);
  const [fileList, setFileList] = useState<any>();

  const [disabledDisciplines, setDisabledDisciplines] = useState<number[]>([]);

  const showModal = (discipline: RetakeDisciplinesItem) => {
    setDisabledDisciplines((prev) => [...prev, discipline.disciplineId]);
    setSelectedDiscipline(discipline);
    setIsModalVisible(true);
  };

  const handleOk = () => {
    setIsModalVisible(false);
    handleUpload();
  };

  const handleCancel = () => {
    setDisabledDisciplines((prev) => prev.filter((id) => id !== selectedDiscipline?.disciplineId));
    setIsModalVisible(false);
  };

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleUpload = () => {
    const formData = new FormData();
    formData.append('retakeFile', fileList);

    if (selectedDiscipline) {
      setIsSubmitting(true);
      SummerRetakeApplicationService.uploadFileRetakeApplication(formData)
        .then((response) => {
          setFileId(response.data);
          SummerRetakeApplicationService.createRetakeApplication(
            response.data,
            selectedDiscipline.disciplineId,
            selectedDiscipline.volumeCredits
          );
          //successNotification('Application accepted for consideration');
          setFileList(null);
          setTimeout(() => {
            fetchRetakeDisciplinesData();
            fetchRetakeStatusData();
            fetchTotalCredits();
            setIsSubmitting(false);
          }, 3000);
        })
        .catch((error) => {
          message.error('Ошибка загрузки файла');
          console.error('Error:', error);
          setIsSubmitting(false);
        });
    }
  };

  const uploadProps: UploadProps = {
    name: 'file',
    onChange(e) {
      e.file.status = 'done';
    },
    customRequest(info) {
      setFileList(info.file);
    },
  };

  //   const isButtonDisabledForDiscipline = (disciplineId: number) => {
  //     return retakeStatusData.some(
  //       (item) => item.disciplineId === disciplineId && (item.approvedOfficeRegistrar === 1 || item.approvedOfficeRegistrar === 3)
  //     );
  //   };

  const columns = [
    {
      title: 'Discipline',
      dataIndex: 'discipline',
      key: 'discipline',
    },
    {
      title: 'Credits',
      dataIndex: 'volumeCredits',
      key: 'volumeCredits',
    },
    {
      title: 'Actions',
      key: 'action',
      render: (_: any, record: RetakeDisciplinesItem) => {
        const totalCredits = Number(totalSum) + Number(record.volumeCredits);
        const isCreditExceeded = totalCredits > 6; //макс количество кредитов //надо будет поменять на 27 кредитов
        const isApplicationProcessing = isButtonDisabledForDiscipline(record);
        const isDisabled =
          isSubmitting ||
          isCreditExceeded ||
          isApplicationProcessing ||
          disabledDisciplines.includes(record.disciplineId);

        let buttonText = '';
        if (isApplicationProcessing) {
          buttonText = 'Wait for your application to be processed';
        } else if (isCreditExceeded) {
          buttonText = 'Number of credits should not exceed 6'; //надо будет поменять на 27 кредитов
        } else {
          buttonText = 'Submit a Retake application';
        }

        return (
          <Button onClick={() => (isDisabled ? null : showModal(record))} disabled={isDisabled} loading={isSubmitting}>
            {buttonText}
          </Button>
        );
      },
    },
  ];

  const getStatusText = (status: number) => {
    switch (status) {
      case 1:
        return <Tag color="blue">In processing</Tag>;
      case 2:
        return <Tag color="red">Declined</Tag>;
      case 3:
        return <Tag color="green">Approved</Tag>;
      default:
        return <Tag color="gray">Unknown status</Tag>;
    }
  };

  const formatDateString = (dateString: string): string => {
    if (!dateString || new Date(dateString).toString() === 'Invalid Date') {
      return 'Unknown';
    }
    const date = new Date(dateString);
    const formattedDate = `${date.getDate().toString().padStart(2, '0')}.${(date.getMonth() + 1)
      .toString()
      .padStart(2, '0')}.${date.getFullYear()} ${date.getHours().toString().padStart(2, '0')}:${date
      .getMinutes()
      .toString()
      .padStart(2, '0')}`;
    return formattedDate;
  };

  const statusColumns = [
    // {
    //   title: 'duid',
    //   dataIndex: 'disciplineId',
    //   key: 'disciplineId',
    // },
    {
      title: 'Discipline',
      dataIndex: 'discipline',
      key: 'discipline',
    },
    // {
    //   title: 'Status',
    //   dataIndex: 'approved',
    //   key: 'approved',
    //   render: (status: number) => <span>{getStatusText(status)}</span>,
    // },
    {
      title: 'Credits',
      dataIndex: 'volumeCredits',
      key: 'volumeCredits',
    },
    {
      title: 'Status',
      dataIndex: 'approvedOfficeRegistrar',
      key: 'approved',
      render: (status: number) => <span>{getStatusText(status)}</span>,
    },
    {
      title: 'Comment',
      dataIndex: 'comment',
      key: 'comment',
    },
    {
      title: 'Submission date',
      dataIndex: 'createdDate',
      render: (text: string) => <span>{formatDateString(text)}</span>,
    },
    {
      title: 'Approved date',
      dataIndex: 'approvedDate',
      render: (text: string) => <span>{formatDateString(text)}</span>,
    },
  ];

  const isButtonDisabledForDiscipline = (record: RetakeDisciplinesItem) => {
    const existingApplication = retakeStatusData.find((item) => item.disciplineId === record.disciplineId);

    if (!existingApplication) {
      return false;
    }

    //return existingApplication.approvedOfficeRegistrar === 1 || existingApplication.approvedOfficeRegistrar === 3; //надо восстановить

    //надо удалить
    if (existingApplication.approvedOfficeRegistrar === 1) {
      return true;
    }

    if (existingApplication.approvedOfficeRegistrar === 3) {
      const createdDate = new Date(existingApplication.createdDate);
      const cutoffDate = new Date('2025-08-18');

      return createdDate > cutoffDate;
    }

    return false;
    //надо удалить
  };

  return (
    <PageWrapper>
      <Typography.Title level={1}>Summer Retake Application </Typography.Title>
      <Typography.Title level={4}>Dear 1st and 2nd year students!!!</Typography.Title>
      <p>
        The registration for retaking academic courses (Retake) based on the results of the 1st and 2nd trimesters of
        the 2024–2025 academic year will be open from <strong>May 5 to May 24, 2025 (inclusive).</strong>
      </p>
      <p>
        Students who received unsatisfactory grades (“F”, 0–49 points) are eligible to register for retake courses.
        Please note that the total number of academic credits <strong>for one academic year, </strong> including all
        three trimesters,<strong> must not exceed 27 credits.</strong>
      </p>
      <p>
        <strong>Retake Registration Procedure: </strong>
        <br />
        1. Registration must be completed through your personal account in the Digital University information system.
        <br />
        2. A payment receipt in PDF format with full data (from payment history) must be uploaded in the Retake
        Application section of Digital University (DU).
      </p>
      <p>
        Access to retake courses is granted <strong>if only all of the above requirements are fulfilled.</strong> <br />
        <strong>
          Registration for retake courses based on the results of the 3rd trimester will be announced later.
        </strong>
        <br />
        The summer semester will take place from <strong>June 30 to August 9, 2025. </strong>
        <br />
        <br />
        <strong>
          Note! Applications for tuition fee refunds will be accepted before the beginning of the summer semester.
        </strong>
        <br />
        Applications are accepted from <strong>09:00 to 18:00</strong> during the registration period.
      </p>

      {/* <Typography.Title level={4}>Dear 3rd-year students!</Typography.Title>
      <p>
        From March 6 to March 10, 2025, registration will be open for the retake of academic disciplines (Retake) for graduating students <strong>with no more than two academic debts based </strong>on the results of the 7th and 8th trimesters.
      </p>
      <p>
        Retake Registration Procedure: <br />
        1. Registration is conducted through your personal account in Digital University.<br />
        2. The learner attaches the payment receipt in PDF format (from the payment history) in the Retake Application tab in Digital University (DU).<br />
        ⚠ Only if all items are completed, the learner is admitted to Retake!<br />
        📅 The retake period will take place from March 17 to April 26, 2025.


      </p> */}
      <p>
        <Typography.Title level={4}>
          <strong>Cost of 1 credit:</strong>
        </Typography.Title>
        • Bachelor's degree 1,2,3 courses - 20000 tg <br />
        • Master's Degree 1-2 courses - 28000 tg <br />
        • Doctoral Degree 1 course - 45000 tg <br />• For foreign nationals on scholarship program (Bologna process)
        -10597 tg
      </p>
      <Typography.Title level={4}>
        <strong>Total amount of credits: </strong> {totalSum}
      </Typography.Title>
      <Button style={{ margin: '30px 30px 30px 0' }} onClick={toggleRetakeDisciplines}>
        Submitting an application
      </Button>
      <Button onClick={toggleRetakeStatus} style={{ margin: '0 30px 30px 0' }}>
        Show application status
      </Button>

      {showRetakeDisciplines && (
        <div>
          <div>
            <Table dataSource={retakeDisciplinesData} columns={columns} scroll={{ x: '500px' }} />
          </div>
        </div>
      )}

      {showRetakeStatus && <Table dataSource={retakeStatusData} columns={statusColumns} scroll={{ x: '500px' }} />}

      <Modal
        title="Submitting an Application"
        visible={isModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
        footer={[
          <Button key="back" onClick={handleCancel}>
            Cancel
          </Button>,
          <Button key="submit" type="primary" onClick={handleOk}>
            Send
          </Button>,
        ]}
      >
        <Upload {...uploadProps} fileList={fileList ? [fileList] : []}>
          <Button icon={<UploadOutlined />}>Select a file</Button>
        </Upload>
      </Modal>
    </PageWrapper>
  );
};

export default SummerRetakeDisciplines;
